<template>
  <basic-dialog :show="show" max-width="640px" @close="onCancel">
    <template v-slot:title>Add Departure</template>

    <template v-slot:actions>
      <v-btn
        color="primary"
        :loading="saving"
        :disabled="!canSubmit"
        @click="onConfirm"
        >{{ mode === "single" ? "Create" : "Create Range" }}</v-btn
      >
    </template>
    <v-row>
      <v-col class="pb-0">
        <v-btn-toggle v-model="mode" mandatory dense class="mb-4">
          <v-btn value="single" small>Single</v-btn>
          <v-btn value="bulk" small>Multi-range</v-btn>
        </v-btn-toggle>
      </v-col>
    </v-row>
    <!-- SINGLE MODE -->
    <template v-if="mode === 'single'">
      <v-row>
        <v-col cols="12" sm="6">
          <date-picker
            v-model="single.startDate"
            custom-label="Start Date"
            outlined
            :range-start="masterValidFrom"
            :range-end="masterValidTo"
            :allowed-dates-fn="masterDayAndRangeFn"
            :error-messages="errors.startDate"
          ></date-picker>
        </v-col>
        <v-col cols="12" sm="6">
          <date-picker
            v-model="single.returnDate"
            custom-label="Return Date"
            outlined
            :range-start="single.startDate || masterValidFrom"
            :range-end="masterValidTo"
            :error-messages="errors.returnDate"
          ></date-picker>
        </v-col>
        <v-col cols="12">
          <v-text-field
            v-model="single.name"
            label="Name"
            outlined
            hide-details
          ></v-text-field>
        </v-col>
        <v-col cols="6">
          <v-text-field
            v-model.number="single.adults"
            label="Adults"
            type="number"
            min="0"
            outlined
            hide-details
          ></v-text-field>
        </v-col>
        <v-col cols="6">
          <v-text-field
            v-model.number="single.children"
            label="Children"
            type="number"
            min="0"
            outlined
            hide-details
          ></v-text-field>
        </v-col>
      </v-row>
    </template>

    <!-- BULK MODE -->
    <template v-else>
      <v-row>
        <v-col cols="12" sm="6">
          <date-picker
            v-model="bulk.rangeFrom"
            custom-label="Range From"
            outlined
            :range-start="masterValidFrom"
            :range-end="masterValidTo"
            :allowed-dates-fn="masterDayAndRangeFn"
            :error-messages="errors.rangeFrom"
          ></date-picker>
        </v-col>
        <v-col cols="12" sm="6">
          <date-picker
            v-model="bulk.rangeTo"
            custom-label="Range To"
            outlined
            :range-start="bulk.rangeFrom || masterValidFrom"
            :range-end="masterValidTo"
            :error-messages="errors.rangeTo"
          ></date-picker>
        </v-col>
        <v-col cols="12">
          <gt-multi-select
            v-model="bulk.daysOfWeek"
            :items="dayOptionsForBulk"
            item-text="name"
            item-value="value"
            label="Days of Week"
            outlined
            :select-all="true"
          ></gt-multi-select>
        </v-col>
        <v-col cols="6">
          <v-text-field
            v-model.number="bulk.tripLengthDays"
            label="Trip Length (days)"
            type="number"
            min="1"
            outlined
            hide-details
          ></v-text-field>
        </v-col>
        <v-col cols="6">
          <v-text-field
            v-model="bulk.namePattern"
            label="Name Pattern"
            hint="Tokens: {name} {date}"
            persistent-hint
            outlined
          ></v-text-field>
        </v-col>
        <v-col cols="6">
          <v-text-field
            v-model.number="bulk.adults"
            label="Adults"
            type="number"
            min="0"
            outlined
            hide-details
          ></v-text-field>
        </v-col>
        <v-col cols="6">
          <v-text-field
            v-model.number="bulk.children"
            label="Children"
            type="number"
            min="0"
            outlined
            hide-details
          ></v-text-field>
        </v-col>
      </v-row>
    </template>
  </basic-dialog>
</template>

<script>
import BasicDialog from "../../shared/BasicDialog";
import DatePicker from "../../shared/DatePicker";
import GtMultiSelect from "../../shared/GtMultiSelect";
import moment from "moment";
import {
  CREATE_OPEN_GROUP_DEPARTURE,
  CREATE_OPEN_GROUP_DEPARTURES_BULK,
} from "../departure-gql";

const WDAY_TO_CWDAY = { 0: 7, 1: 1, 2: 2, 3: 3, 4: 4, 5: 5, 6: 6 };

export default {
  name: "OpenGroupDepartureAddDialog",

  components: { BasicDialog, DatePicker, GtMultiSelect },

  props: {
    show: { type: Boolean, default: false },
    openGroupId: { type: String, default: null },
    masterName: { type: String, default: "" },
    masterAdults: { type: Number, default: 0 },
    masterChildren: { type: Number, default: 0 },
    masterDuration: { type: Number, default: 7 },
    masterValidFrom: { type: String, default: null },
    masterValidTo: { type: String, default: null },
    masterDaysOfWeek: { type: Array, default: () => [] },
  },

  data() {
    return {
      mode: "single",
      saving: false,
      errors: {},
      dayOptions: [
        { name: "Monday", value: "1" },
        { name: "Tuesday", value: "2" },
        { name: "Wednesday", value: "3" },
        { name: "Thursday", value: "4" },
        { name: "Friday", value: "5" },
        { name: "Saturday", value: "6" },
        { name: "Sunday", value: "0" },
      ],
      single: this.blankSingle(),
      bulk: this.blankBulk(),
    };
  },

  computed: {
    canSubmit() {
      if (this.saving) return false;
      if (this.mode === "single") {
        return !!(this.single.startDate && this.single.returnDate);
      }
      return !!(
        this.bulk.rangeFrom &&
        this.bulk.rangeTo &&
        this.bulk.daysOfWeek.length > 0 &&
        this.bulk.tripLengthDays > 0
      );
    },
    dayOptionsForBulk() {
      const allowed = this.masterDaysOfWeek || [];
      return this.dayOptions.map((opt) => ({
        ...opt,
        disabled: allowed.length > 0 && !allowed.includes(opt.value),
      }));
    },
  },

  watch: {
    show(open) {
      if (open) this.resetForm();
    },
    "single.startDate"(val) {
      if (val && this.masterDuration > 0) {
        this.single.returnDate = moment(val)
          .add(this.masterDuration, "days")
          .format("YYYY-MM-DD");
      }
    },
  },

  methods: {
    blankSingle() {
      return {
        startDate: "",
        returnDate: "",
        name: this.masterName,
        adults: this.masterAdults,
        children: this.masterChildren,
      };
    },
    blankBulk() {
      return {
        rangeFrom: "",
        rangeTo: "",
        daysOfWeek: [...(this.masterDaysOfWeek || [])],
        tripLengthDays: this.masterDuration,
        namePattern: "{name} {date}",
        adults: this.masterAdults,
        children: this.masterChildren,
      };
    },
    masterDayAndRangeFn(val) {
      if (this.masterValidFrom && this.masterValidTo) {
        const from = moment(this.masterValidFrom);
        const to = moment(this.masterValidTo);
        if (
          from.isValid() &&
          to.isValid() &&
          !moment(val).isBetween(from, to, "days", "[]")
        ) {
          return false;
        }
      }
      const allowed = this.masterDaysOfWeek || [];
      if (allowed.length === 0) return true;
      const wday = String(moment(val).day());
      return allowed.includes(wday);
    },
    resetForm() {
      this.mode = "single";
      this.errors = {};
      this.single = this.blankSingle();
      this.bulk = this.blankBulk();
    },
    onCancel() {
      this.$emit("close");
    },
    onConfirm() {
      this.errors = {};
      this.saving = true;
      const promise =
        this.mode === "single" ? this.createSingle() : this.createBulk();
      promise
        .then((res) => {
          this.saving = false;
          this.$emit("created", res);
          this.$emit("close");
        })
        .catch((err) => {
          this.saving = false;
          const msg = err.graphQLErrors
            ? err.graphQLErrors.map((e) => e.message).join(", ")
            : err.message;
          this.$store.dispatch("showAppSnackbar", { msg, color: "error" });
        });
    },
    createSingle() {
      return this.$apollo
        .mutate({
          mutation: CREATE_OPEN_GROUP_DEPARTURE,
          variables: {
            openGroupId: this.openGroupId,
            openGroupDepartureInput: {
              name: this.single.name,
              startDate: this.single.startDate,
              returnDate: this.single.returnDate,
              adults: Number(this.single.adults) || 0,
              children: Number(this.single.children) || 0,
            },
          },
        })
        .then(({ data }) => ({
          kind: "single",
          departure: data.createOpenGroupDeparture,
        }));
    },
    createBulk() {
      const cwdays = this.bulk.daysOfWeek
        .map((v) => WDAY_TO_CWDAY[v])
        .sort((a, b) => a - b)
        .join(",");
      return this.$apollo
        .mutate({
          mutation: CREATE_OPEN_GROUP_DEPARTURES_BULK,
          variables: {
            input: {
              openGroupId: this.openGroupId,
              rangeFrom: this.bulk.rangeFrom,
              rangeTo: this.bulk.rangeTo,
              daysOfWeek: cwdays,
              tripLengthDays: Number(this.bulk.tripLengthDays) || 1,
              namePattern: this.bulk.namePattern,
              adults: Number(this.bulk.adults) || 0,
              children: Number(this.bulk.children) || 0,
            },
          },
        })
        .then(({ data }) => ({
          kind: "bulk",
          departures: data.createOpenGroupDeparturesBulk.departures,
          skippedDates: data.createOpenGroupDeparturesBulk.skippedDates,
        }));
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/gt/open-groups/builder/OpenGroupDepartureAddDialog.vue